import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { Link } from "react-router-dom";

export default function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" updated="June 1, 2026" current="/terms">
      <p>
        Welcome to NextGen Resolution Experts ("NextGen," "we," "us," or "our"). These Terms of Service ("Terms")
        govern your access to and use of our AI-powered IT helpdesk platform, including our website, applications,
        AI assistant, ticketing system, and knowledge base (collectively, the "Service"). By accessing or using the
        Service, you agree to be bound by these Terms. If you are using the Service on behalf of a company, you
        represent that you have authority to bind that company to these Terms.
      </p>

      <LegalSection heading="1. Eligibility & Accounts">
        <p>
          You must be at least 18 years old and capable of forming a binding contract to use the Service. You are
          responsible for maintaining the confidentiality of your account credentials and for all activity that
          occurs under your account. Notify us immediately at support@nextgenresolutionexperts.com if you suspect
          unauthorized use of your account.
        </p>
      </LegalSection>

      <LegalSection heading="2. Acceptable Use — Rules of the Road">
        <p>You agree NOT to use the Service to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Send spam, unsolicited communications, or bulk messages of any kind;</li>
          <li>Upload, transmit, or distribute illegal, harmful, defamatory, or infringing content;</li>
          <li>Harass, abuse, or threaten support staff, other users, or any third party;</li>
          <li>Attempt to gain unauthorized access to the Service, other accounts, or connected systems;</li>
          <li>Reverse-engineer, scrape, or copy any part of the platform or its AI models;</li>
          <li>Interfere with or disrupt the integrity or performance of the Service;</li>
          <li>Use the Service in violation of any applicable law or regulation.</li>
        </ul>
        <p>
          <strong>We reserve the right to suspend or permanently ban any user or account that abuses the system</strong>,
          with or without notice, at our sole discretion. Serious violations may be reported to law enforcement.
        </p>
      </LegalSection>

      <LegalSection heading="3. AI Assistant — Important Disclaimer">
        <p>
          The Service includes an AI-powered assistant that provides automated responses and suggested resolutions.
          AI-generated output is provided for convenience and informational purposes only. While we strive for
          accuracy, <strong>AI responses may contain errors, and NextGen Resolution Experts is not liable for any
          action taken, decision made, or approval granted by the AI assistant</strong> — including, without
          limitation, any refund, credit, or commitment the AI may incorrectly indicate or approve. All AI outputs
          are subject to human review and final confirmation by an authorized representative.
        </p>
      </LegalSection>

      <LegalSection heading="4. Intellectual Property">
        <p>
          The Service, including all software, design, text, graphics, and AI systems, is owned by NextGen
          Resolution Experts and protected by intellectual property laws. We grant you a limited, non-exclusive,
          non-transferable license to use the Service in accordance with these Terms and your Service Agreement.
          You retain ownership of the data and content you submit to the Service.
        </p>
      </LegalSection>

      <LegalSection heading="5. Fees & Payment">
        <p>
          Paid plans are governed by our <Link to="/service-agreement" className="text-blue-600 hover:underline">Service Agreement</Link> and{" "}
          <Link to="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</Link>. Failure to pay
          applicable fees may result in suspension or termination of your access to the Service.
        </p>
      </LegalSection>

      <LegalSection heading="6. Disclaimer of Warranties">
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
          INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT
          WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT AI OUTPUTS WILL BE ACCURATE OR COMPLETE.
        </p>
      </LegalSection>

      <LegalSection heading="7. Limitation of Liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEXTGEN RESOLUTION EXPERTS SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR BUSINESS
          OPPORTUNITY, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE
          FEES PAID BY YOU IN THE THREE (3) MONTHS PRECEDING THE CLAIM.
        </p>
      </LegalSection>

      <LegalSection heading="8. Termination">
        <p>
          We may suspend or terminate your access to the Service for violation of these Terms. You may stop using
          the Service at any time; cancellation of paid plans is governed by the Service Agreement (30 days' written
          notice required).
        </p>
      </LegalSection>

      <LegalSection heading="9. Changes to These Terms">
        <p>
          We may update these Terms from time to time. Material changes will be communicated via email or in-app
          notice. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection heading="10. Contact">
        <p>
          Questions about these Terms? Contact us at{" "}
          <a href="mailto:support@nextgenresolutionexperts.com" className="text-blue-600 hover:underline">
            support@nextgenresolutionexperts.com
          </a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
